import { useTranslation } from 'react-i18next';

import { Icon, Tooltip } from '~/components';
import { useIsExternalUser } from '~/hooks/useIsExternalUser';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import { ContentBox } from '~settings/components/ContentBox';
import { HeaderSmall } from '~settings/components/HeaderSmall';
import { PropertyList } from '~settings/components/PropertyList';
import { RelativeDate } from '~settings/components/RelativeDate';
import { UserTypeMap } from '~settings/constants/UserTypeMap';

import { useUserDetail } from '../../hooks/useUserDetail';

export function UserDetailsBlock() {
  const { t } = useTranslation('settings');
  const { t: rolesT } = useTranslation('settings', { keyPrefix: 'userTypes' });
  const user = useUserDetail();
  const [isInternalUserUpdatesEnabled] = useFeatureFlags([
    FEATURE_FLAGS.SettingsInternalUserUpdates,
  ]);

  const isCurrentUserExternal = useIsExternalUser();

  const isInternalUser =
    (user.userType && user.userType !== 'EXTERNAL') || user.isInternal;
  let userType = rolesT(`${UserTypeMap[user.userType!]}.name`);

  if (!isInternalUserUpdatesEnabled) {
    userType = isInternalUser ? t('internal') : t('external');
  }

  return (
    <ContentBox>
      <HeaderSmall>{t('details')}</HeaderSmall>
      <PropertyList.Root>
        {user.jobTitle ? (
          <PropertyList.Item>
            <PropertyList.Key>{t('jobTitle')}</PropertyList.Key>
            <PropertyList.Value>{user.jobTitle}</PropertyList.Value>
          </PropertyList.Item>
        ) : null}
        <PropertyList.Item>
          <PropertyList.Key>{t('email')}</PropertyList.Key>
          <PropertyList.Value wrap>{user.email}</PropertyList.Value>
        </PropertyList.Item>
        {user.phone ? (
          <PropertyList.Item>
            <PropertyList.Key>{t('phone')}</PropertyList.Key>
            <PropertyList.Value>{user.phone}</PropertyList.Value>
          </PropertyList.Item>
        ) : null}
        <PropertyList.Item>
          <PropertyList.Key>{t('status')}</PropertyList.Key>
          <PropertyList.Value>
            {user.isActive ? t('active') : t('disabled')}
            <Tooltip
              content={t(user.isActive ? 'activeTooltip' : 'disabledTooltip')}
              side="top"
              delayDuration={0}
            >
              <Icon
                type="circle-info"
                variant="light"
                className="ml-sm text-subtlest"
                size="sm"
              />
            </Tooltip>
          </PropertyList.Value>
        </PropertyList.Item>
        {user.lastLogin ? (
          <PropertyList.Item>
            <PropertyList.Key>{t('lastLogin')}</PropertyList.Key>
            <PropertyList.Value>
              <RelativeDate data={user.lastLogin} />
            </PropertyList.Value>
          </PropertyList.Item>
        ) : null}
        {!isCurrentUserExternal && (
          <PropertyList.Item>
            <PropertyList.Key>{t('userType')}</PropertyList.Key>
            <PropertyList.Value>{userType}</PropertyList.Value>
          </PropertyList.Item>
        )}
      </PropertyList.Root>
    </ContentBox>
  );
}
UserDetailsBlock.displayName = 'UserDetailsBlock';
